/** Pure derivations for the Overview page — no React, unit-tested. */

interface OverviewModelLike {
  model_id?: string;
  name?: string;
  is_starred?: boolean;
  starred_at?: number | null;
  last_opened_by_me?: number;
  created_at?: number;
  scheduled_deletion_date?: number;
  /** "Owner", or the relation a share/space granted. */
  relation?: string;
  is_catalog?: boolean;
  is_app?: boolean;
}

/** A just-created model keeps its top spot this long before pure
 *  last-opened ordering takes over — so it doesn't sink to the bottom
 *  on the next poll just because it hasn't been opened yet. */
const FRESH_CREATION_WINDOW_S = 20 * 60;

/** Cap on never-opened shares at the top of Recent. Nothing ages them out —
 *  there is no per-user "shared at" on the wire — so uncapped they evict Recent. */
const SHARED_UNOPENED_IN_RECENT = 2;

/** Pins shown on All spaces before "Show all". */
export const PINNED_LIMIT = 5;

const live = <T extends OverviewModelLike>(m: T): boolean =>
  !m.scheduled_deletion_date;

const byLastOpenedDesc = <T extends OverviewModelLike>(a: T, b: T): number =>
  (b.last_opened_by_me ?? 0) - (a.last_opened_by_me ?? 0);

const byCreatedDesc = <T extends OverviewModelLike>(a: T, b: T): number =>
  (b.created_at ?? 0) - (a.created_at ?? 0);

/** Newest pin first; ties and a missing pin date keep last-opened order. */
const byPinnedDesc = <T extends OverviewModelLike>(a: T, b: T): number =>
  (b.starred_at ?? 0) - (a.starred_at ?? 0) || byLastOpenedDesc(a, b);

const isUnopenedNew = (m: OverviewModelLike, nowS: number): boolean =>
  m.last_opened_by_me == null &&
  m.created_at != null &&
  nowS - m.created_at <= FRESH_CREATION_WINDOW_S;

/** Shared with the user and never opened: no `last_opened_by_me`, and a
 *  `created_at` from when the OWNER made it, so neither activity signal places
 *  it. Matched on the relation itself — a missing one must not rank a model up. */
const isSharedUnopened = (m: OverviewModelLike): boolean =>
  m.last_opened_by_me == null &&
  !m.is_catalog &&
  (m.relation === "Collaborator" || m.relation === "Viewer");

/** Higher sorts first: just-created, then shared-unopened, then opened. */
const activityRank = (m: OverviewModelLike, nowS: number): number =>
  isUnopenedNew(m, nowS) ? 2 : isSharedUnopened(m) ? 1 : 0;

/** Activity order: never-opened on top by rank, then last-opened desc.
 *  `created_at` orders within a never-opened rank and breaks ties below. */
const byActivity =
  (nowS: number) =>
  <T extends OverviewModelLike>(a: T, b: T): number => {
    const byRank = activityRank(b, nowS) - activityRank(a, nowS);
    if (byRank !== 0) return byRank;
    if (activityRank(a, nowS) > 0) return byCreatedDesc(a, b);
    return byLastOpenedDesc(a, b) || byCreatedDesc(a, b);
  };

export const selectPinned = <T extends OverviewModelLike>(
  models: T[],
  cap = PINNED_LIMIT,
): T[] =>
  models
    .filter((m) => live(m) && m.is_starred)
    .sort(byPinnedDesc)
    .slice(0, cap);

export const selectRecent = <T extends OverviewModelLike>(
  models: T[],
  cap = 5,
  nowMs = Date.now(),
): T[] => {
  const nowS = Math.floor(nowMs / 1000);
  const ordered = models
    .filter(
      (m) =>
        live(m) &&
        (m.last_opened_by_me != null ||
          isUnopenedNew(m, nowS) ||
          isSharedUnopened(m)),
    )
    .sort(byActivity(nowS));

  const kept: T[] = [];
  let sharedShown = 0;
  for (const model of ordered) {
    if (kept.length >= cap) break;
    // Rank 1 is shared-unopened but not just-created — the only rank a user
    // accumulates without ever acting, so it alone is capped.
    if (activityRank(model, nowS) === 1) {
      if (sharedShown >= SHARED_UNOPENED_IN_RECENT) continue;
      sharedShown += 1;
    }
    kept.push(model);
  }
  return kept;
};

/** Full list for the expanded "All models" view — Recent's activity order with
 *  no starred bias and no share cap, so the two never disagree on ranking. */
export const selectAll = <T extends OverviewModelLike>(
  models: T[],
  nowMs = Date.now(),
): T[] => {
  const nowS = Math.floor(nowMs / 1000);
  return models.filter(live).sort(byActivity(nowS));
};

/** The Recent list to render. A user who has opened nothing and sees no pin
 *  gets the full list instead — with no recent activity for the share cap to
 *  protect, a first login should show every shared model, not two of them. */
export const selectRecentForDisplay = <T extends OverviewModelLike>(
  all: T[],
  cap = 5,
  nowMs = Date.now(),
  pinned = selectPinned(all, cap),
): T[] => {
  // An app's model is listed with its app, not among the models someone opened.
  const models = all.filter((m) => !m.is_app);
  const recent = selectRecent(models, cap, nowMs);
  const hasOpened = models.some((m) => live(m) && m.last_opened_by_me != null);
  if (hasOpened || pinned.length > 0) return recent;
  return selectAll(models, nowMs).slice(0, cap);
};

// A row just trashed has no purge date until the server's read brings it,
// and it is the latest to go.
const purgeOrder = (m: OverviewModelLike): number =>
  m.scheduled_deletion_date ?? Number.MAX_SAFE_INTEGER;

const nameMatches = (m: OverviewModelLike, query: string): boolean =>
  !query || (m.name || "Unnamed Model").toLowerCase().includes(query);

/** The Catalog models page: live rows in the Overview's activity order, the
 *  trashed ones soonest purge first. A model sits in one list only — a read
 *  that lags a restore or a trash elsewhere must not show it twice. */
export const selectCatalogModels = <T extends OverviewModelLike>(
  live: T[],
  trashed: T[],
  query = "",
  nowMs = Date.now(),
): { live: T[]; trashed: T[] } => {
  const q = query.trim().toLowerCase();
  const liveIds = new Set(live.map((m) => m.model_id));
  return {
    live: selectAll(
      live.filter((m) => nameMatches(m, q)),
      nowMs,
    ),
    trashed: trashed
      .filter((m) => !liveIds.has(m.model_id) && nameMatches(m, q))
      .sort((a, b) => purgeOrder(a) - purgeOrder(b)),
  };
};

/** Whether the account has a model at all — a trashed one is not one. */
export const hasLiveModels = <T extends OverviewModelLike>(
  models: T[],
): boolean => models.some(live);

export const ownershipLabel = (
  relation: string | undefined,
  isCatalog?: boolean,
): string =>
  isCatalog ? "Catalog" : relation === "Owner" ? "Owner" : "Shared";

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/** Compact relative time for the 40px row meta slot: "now", "5m", "2h", "3d", "2w", "4mo", "1y". */
export const formatCompactRelativeTime = (
  epochSeconds: number,
  nowMs = Date.now(),
): string => {
  const diff = Math.max(0, Math.floor(nowMs / 1000) - epochSeconds);
  if (diff < MINUTE) return "now";
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  if (diff < WEEK) return `${Math.floor(diff / DAY)}d`;
  if (diff < MONTH) return `${Math.floor(diff / WEEK)}w`;
  if (diff < YEAR) return `${Math.floor(diff / MONTH)}mo`;
  return `${Math.floor(diff / YEAR)}y`;
};
